/**
 * Future considerations
 *
 * CampaignData
 * Multiple Measurement IDs
 */

import { scriptLoaderFactory } from '@easeltv/utilities';

import { flattenObject } from '../helpers/flattenObject';
import { Driver, GoogleDriverConfig, GTagConfig } from '../types';

/* -------------------------------- Constants ------------------------------- */

/** URL of the gtag script */
const SCRIPT_SRC = 'https://www.googletagmanager.com/gtag/js';

const gaScriptLoader = (measurementId: string) =>
    scriptLoaderFactory(
        `${SCRIPT_SRC}?id=${measurementId}`,
        () => typeof window.gtag !== 'undefined',
    );

/** Default configuration fields for GA4 */
const DEFAULT_GTAG_CONF: GTagConfig = {
    /** Disable automatic sending of page views */
    send_page_view: false,

    /** Disable All Advertising */
    allow_google_signals: true,

    /** Disable Advertising Personalization */
    allow_ad_personalization_signals: true,

    /** Cookie Flags */
    cookie_flags: '',
};

/* --------------------------------- Driver --------------------------------- */

/**
 * GoogleDriver
 *
 * Logs all events to Google Analytics
 *
 * @param config - Config for a GoogleDriver
 * @returns An instance of GoogleDriver
 */
export const GoogleDriver = ({
    measurementId,
    debug = false,
    config = {},
}: GoogleDriverConfig): Driver => {
    /**
     * track
     *
     * Generic track event to gTag
     *
     * @param event - Event name
     * @param data - Payload data to send with the event
     */
    const track = (event: string, data: Record<string, unknown>) => {
        window.gtag?.('event', event, data);
    };

    /**
     * identify
     *
     * Sets the userId to be sent alongside the tracking events to identify a particular user
     *
     * @param userId
     */
    const identify = (userId: string | undefined) => {
        window.gtag?.('set', { user_id: userId });
    };

    /**
     * pageView
     *
     * Sends a specific ga page_view event
     *
     * @param data - Page view data
     */
    const pageView = (data: Record<string, unknown>) => {
        // const pageView = {
        //     page_title: properties.title,
        //     page_location: properties.url,
        //     page_path: properties.path || document.location.pathname,
        //     page_hash: properties.hash,
        //     page_search: properties.page_search,
        //     page_referrer: properties.referrer,
        // };

        window.gtag?.('event', 'page_view', data);
    };

    // Driver Methods
    return {
        initialize: async () => {
            // Load the script
            await gaScriptLoader(measurementId)();

            // This is the gtag code from GA installation
            if (!window.dataLayer) {
                window.dataLayer = window.dataLayer || [];
            }

            function gtag() {
                /* eslint-disable-next-line prefer-rest-params */
                window.dataLayer.push(arguments);
            }
            window.gtag = gtag;

            window.gtag('js', new Date());

            const debugConfig = debug ? { debug_mode: true } : {};

            const resolvedConfig = {
                ...DEFAULT_GTAG_CONF,
                ...debugConfig,
                ...config,
            };

            window.gtag('config', measurementId, resolvedConfig);

            window.gtag('consent', 'default', {
                analytics_storage: 'denied',
                ad_storage: 'denied',
            });
        },
        enable: () => {
            window.gtag?.('consent', 'update', {
                ad_personalization: 'granted',
                ad_storage: 'granted',
                ad_user_data: 'granted',
                analytics_storage: 'granted',
            });
            window[`ga-disable-${measurementId}`] = false;
        },
        disable: () => {
            window.gtag?.('consent', 'update', {
                ad_personalization: 'granted',
                ad_storage: 'granted',
                ad_user_data: 'granted',
                analytics_storage: 'granted',
            });
            window[`ga-disable-${measurementId}`] = true;
        },
        trigger: (payload, defaultData) => {
            if (payload.type === 'account.signed-in') identify(payload.userId);
            if (payload.type === 'account.registration.completed') {
                identify(payload.userId);
            }
            if (payload.type === 'account.signed-out') identify(undefined);
            if (payload.type === 'app.viewed') pageView(payload.currentPage);

            const { type, ...data } = payload;

            const flattenedData = flattenObject(data);

            track(type, {
                ...flattenedData,
                // This is kept in the track event rather than user properties for reasons of sky-ness
                // Changes made post v1 - so needs to be kept here for continuity
                platform: defaultData.devicePlatform,
            });
        },
        setDefaultData: (data) => {
            window.gtag?.('set', 'user_properties', data);
        },
        updateDefaultData: (data) => {
            window.gtag?.('set', 'user_properties', data);
        },
    };
};
